import { useEffect, useRef, useState } from "react";
import { _BudgetCostingCenter } from "../../../../../core/services/dataBean/EntityCatalog";
import useDataBean from "../../../../../core/services/dataBean/useDataBean";
import { BasicTable, MASK_NUMBER } from "../../../../theme/Component/BasicTable";
import { toast } from "react-toastify";
import { CostingCenterSelect } from "../../../../erp/cellEditor/CostingCenter/CostingCenterSelect";
import { UBLCostingCenter } from "./UBLCostingCenter";
import { InputDate } from "../../../../theme/Input/InputDate";

type CostingCenterMethodID = number;

interface CostingCenterInput {
    IDUBLItemInvoice?: number;
    IssueDate?: any;
    CostingCenterMethod?: CostingCenterMethodID;
    CostingCenter?: string | number;
    CostingCenterName?: string;
    IDCostingCenterMethod?: number;
    CostingCenterDate?: any;
    [key: string]: any;
}

interface CostingCenterMethodType {
    DataBeanProperties: {
        Value: CostingCenterMethodID;
        Property: string;
    }
}

interface CostingCenterMethodRow {
    IDCostingCenterMethod?: number;
    Code?: string | number;
    Name?: string;
    Description?: string;
    [key: string]: any;
}

interface CostingCenterChange {
    CostingCenterMethod?: CostingCenterMethodID;
    CostingCenter?: string | number;
    CostingCenterName?: string;
    IDCostingCenterMethod?: number;
    CostingCenterDate?: any;
    name?: string;
}

export interface CostingCenterValidation {
    CostingCenterMethod: CostingCenterMethodID;
    isValid: boolean;
    percentValue?: number;
    message?: string;
}

interface Props {
    label?: string;
    dataInput: CostingCenterInput;
    onChange: (value: CostingCenterChange) => void;
    onValidationChange?: (value: CostingCenterValidation) => void;
    idCostingCenterMethod: CostingCenterMethodID;
    costingCenterLabel: string;
}

export const CostingCenterEditor = ({ idCostingCenterMethod, dataInput, costingCenterLabel, label, onChange, onValidationChange }: Props) => {

    const budgetCostinCenter = useDataBean(_BudgetCostingCenter);
    const validationRef = useRef(0);
    const [method, setMethod] = useState<CostingCenterMethodType[]>([]);
    const [modeloID, setModeloID] = useState(0);
    const [lista, setLista] = useState<CostingCenterMethodRow[]>([]);
    const [row, setRow] = useState<CostingCenterInput | CostingCenterMethodRow>(dataInput ?? {});

    useEffect(() => {
        setRow(dataInput ?? {});
        setModeloID(idCostingCenterMethod || dataInput?.CostingCenterMethod || 0);
    }, [
        idCostingCenterMethod,
        dataInput?.CostingCenterMethod,
        dataInput?.CostingCenter,
        dataInput?.IDCostingCenterMethod,
        dataInput?.CostingCenterDate,
        dataInput?.IDUBLItemInvoice,
        dataInput?.IssueDate
    ])

    useEffect(() => {
        budgetCostinCenter.instance.getCostingCenterMethodTypes(null).then(
            (resp: any) => {
                const items = resp ?? [];
                setMethod(items);
                if (items.length > 0) {
                    setModeloID(prev => prev || idCostingCenterMethod || dataInput?.CostingCenterMethod || items[0].DataBeanProperties.Value);
                }
            }
        ).catch(err => toast.error(err))
    }, [])

    useEffect(() => {
        if (modeloID === 3) {
            budgetCostinCenter.instance.getCostingCenterMethodCatalog(null).then((resp: any) => {
                setLista(resp ?? []);
            }).catch(err => toast.error(err))
        }
    }, [modeloID])

    const notifyChange = (value: CostingCenterChange) => {
        if (onChange) {
            onChange(value);
        }
    }

    const notifyValidation = (value: CostingCenterValidation) => {
        if (onValidationChange) {
            onValidationChange(value);
        }
    }

    const notifyInvalidPercentage = (percentValue: number) => {
        toast.warning("El metodo de costeo esta incompleto en su porcentaje : " + percentValue + "%");
    }

    const clearCostingCenterValues = (modelID: number) => ({
        CostingCenterMethod: modelID,
        CostingCenter: "",
        IDCostingCenterMethod: 0,
        CostingCenterDate: null,
    })

    const handleModelChange = (modelID: number) => {
        setModeloID(modelID);
        setRow(dataInput ?? {});

        if (modelID === 2) {
            notifyValidation({
                CostingCenterMethod: modelID,
                isValid: false,
                message: "Debe seleccionar un centro de costo."
            });
            return;
        }

        if (modelID === 3) {
            notifyValidation({
                CostingCenterMethod: modelID,
                isValid: false,
                percentValue: 0,
                message: "Debe seleccionar un metodo de costeo completo."
            });
            return;
        }

        if (modelID === 4) {
            notifyValidation({
                CostingCenterMethod: modelID,
                isValid: false,
                percentValue: 0,
                message: "Debe completar la distribucion de centro de costo."
            });
            return;
        }

        if (modelID === 5) {
            const costDate = dataInput?.CostingCenterDate ?? row?.CostingCenterDate ?? null;
            notifyValidation({
                CostingCenterMethod: modelID,
                isValid: Boolean(costDate),
                message: "Debe seleccionar una fecha de corte."
            });
            notifyChange({
                ...clearCostingCenterValues(modelID),
                CostingCenterDate: costDate
            });
            return;
        }

        notifyValidation({ CostingCenterMethod: modelID, isValid: true, percentValue: 100 });
        notifyChange(clearCostingCenterValues(modelID));
    }

    const handleCostingCenterChange = (selectedRow: CostingCenterMethodRow) => {
        const isValid = Boolean(selectedRow?.Code);
        setRow(selectedRow ?? {});

        notifyValidation({
            CostingCenterMethod: 2,
            isValid,
            message: "Debe seleccionar un centro de costo."
        });

        if (!isValid) return;

        notifyChange({
            ...clearCostingCenterValues(2),
            CostingCenter: selectedRow?.Code ?? "",
            CostingCenterName: selectedRow?.Name ?? "",
            name: selectedRow?.Name ?? ""
        });
    }

    const validateCostingCenterMethod = (selectedRow: CostingCenterMethodRow) => {
        const validationID = validationRef.current + 1;
        validationRef.current = validationID;
        setRow(selectedRow ?? {});

        if (!selectedRow?.IDCostingCenterMethod) {
            notifyValidation({
                CostingCenterMethod: 3,
                isValid: false,
                percentValue: 0,
                message: "Debe seleccionar un metodo de costeo."
            });
            return;
        }

        notifyValidation({
            CostingCenterMethod: 3,
            isValid: false,
            percentValue: 0,
            message: "Validando metodo de costeo."
        });

        budgetCostinCenter.instance.getCostingCenterSumValue(selectedRow.IDCostingCenterMethod).then((resp: any) => {
            if (validationRef.current !== validationID) return;

            const por = typeof resp === "number" ? resp : 0;
            if (por === 100) {
                notifyValidation({ CostingCenterMethod: 3, isValid: true, percentValue: por });
                notifyChange({
                    ...clearCostingCenterValues(3),
                    IDCostingCenterMethod: selectedRow.IDCostingCenterMethod,
                    name: selectedRow?.Name
                });
            } else {
                notifyInvalidPercentage(por);
                notifyValidation({
                    CostingCenterMethod: 3,
                    isValid: false,
                    percentValue: por,
                    message: "El metodo de costeo esta incompleto en su porcentaje : " + por + "%"
                });
            }
        }).catch(err => toast.error(err))
    }

    const handleUBLCostingCenterValidation = (isValid: boolean, percentValue: number) => {
        notifyValidation({
            CostingCenterMethod: 4,
            isValid,
            percentValue,
            message: isValid ? undefined : "El metodo de costeo esta incompleto en su porcentaje : " + percentValue + "%"
        });

        if (isValid) {
            notifyChange({ CostingCenterMethod: 4 });
        } else {
            notifyInvalidPercentage(percentValue);
        }
    }

    const handleDateChange = (value: any) => {
        const isValid = Boolean(value);
        setRow({ ...row, CostingCenterDate: value });
        notifyValidation({
            CostingCenterMethod: 5,
            isValid,
            message: "Debe seleccionar una fecha de corte."
        });
        notifyChange({
            ...clearCostingCenterValues(5),
            CostingCenterDate: value
        });
    }

    const head = [
        { title: 'ID', property: 'IDCostingCenterMethod', visible: true, mask: MASK_NUMBER },
        { title: 'Codigo', property: 'Code', visible: true },
        { title: 'Nombre', property: 'Name', visible: true },
        { title: 'Descripcion', property: 'Description', visible: true },
    ]

    return (
        <div className="row">
            <div className="col-md-12">

                <div className="row">
                    <div className="col-md-12">
                        <label>{label ?? "Modelo"}</label>
                        <select name="modelid" className="form-control" value={modeloID} onChange={(e) => { handleModelChange(parseInt(e.target.value)) }}>
                            {method.map((item: any, index: number) => {
                                return (
                                    <option key={index} value={item.DataBeanProperties.Value}>{item.DataBeanProperties.Property}</option>
                                )
                            })}
                        </select>
                    </div>
                    <div className="col-md-12">
                        {modeloID === 2 &&
                            <>
                                <label>Centro de Costo</label>
                                <CostingCenterSelect idSelector={row?.CostingCenter} label=" " onChange={handleCostingCenterChange} returnType="Object" fKey="Code" />
                            </>
                        }
                        {modeloID === 3 &&
                            <BasicTable head={head} body={lista} rowSet={validateCostingCenterMethod}></BasicTable>
                        }
                        {modeloID === 4 &&
                            <UBLCostingCenter IDUBLItemInvoice={dataInput.IDUBLItemInvoice} IssueDate={dataInput.IssueDate} onChange={handleUBLCostingCenterValidation} />
                        }
                        {modeloID === 5 &&
                            <>
                                <label>Fecha de Corte</label>
                                <InputDate name="" value={row.CostingCenterDate} setDate={handleDateChange} />
                            </>
                        }
                    </div>
                </div>
            </div>

        </div>
    )
}
